import { type KeyboardEvent, useId, useState } from 'react';

import { sortedByName } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type { PlaceholderOptionDto } from '@splitfiesta/shared';

import { ClaimConfirmDialog } from '@/components/members/ClaimConfirmDialog';

interface PlaceholderClaimStepProps {
  placeholders: PlaceholderOptionDto[];
  value: string | null;
  /** A spot arrives here only after the joiner said Yes to the confirmation. */
  onChange: (userId: string | null) => void;
  groupName: string;
  disabled?: boolean;
}

const optionClasses = (selected: boolean) =>
  [
    'w-full text-left px-4 py-3 rounded-xl border transition-colors',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    selected
      ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600',
  ].join(' ');

const ARROW_DELTA: Record<string, number> = {
  ArrowDown: 1,
  ArrowRight: 1,
  ArrowUp: -1,
  ArrowLeft: -1,
};

/**
 * Lets a joiner say "I am one of these people" before accepting an invite.
 * The none option is the default so a distracted joiner never files a claim
 * by accident, and a spot is only taken after the confirmation's Yes.
 */
export function PlaceholderClaimStep({
  placeholders,
  value,
  onChange,
  groupName,
  disabled = false,
}: PlaceholderClaimStepProps) {
  const { t } = useTranslation('groups');
  const headingId = useId();
  const [asking, setAsking] = useState<PlaceholderOptionDto | null>(null);

  const sorted = sortedByName(placeholders);

  const pick = (placeholder: PlaceholderOptionDto | null) => {
    if (placeholder === null) {
      onChange(null);
    } else if (placeholder.userId !== value) {
      setAsking(placeholder);
    }
  };

  // Arrows move focus only: selecting on arrow, as a plain radio group does,
  // would open the confirmation on every spot the joiner passes over.
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const delta = ARROW_DELTA[event.key];
    if (delta === undefined) return;

    event.preventDefault();
    const options = Array.from(
      event.currentTarget
        .closest('[role="radiogroup"]')
        ?.querySelectorAll<HTMLButtonElement>('[role="radio"]') ?? []
    );
    const total = options.length;
    const current = options.indexOf(event.currentTarget);
    options[(current + delta + total) % total]?.focus();
  };

  const optionProps = (selected: boolean) => ({
    type: 'button' as const,
    role: 'radio',
    'aria-checked': selected,
    tabIndex: selected ? 0 : -1,
    disabled,
    onKeyDown: handleKeyDown,
    className: optionClasses(selected),
  });

  return (
    <div className="mb-6" data-testid="claim-step">
      <h2
        id={headingId}
        className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2"
      >
        {t('placeholderMembers.claimPrompt')}
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
        {t('placeholderMembers.claimHelp')}
      </p>
      <div role="radiogroup" aria-labelledby={headingId} className="space-y-2">
        {sorted.map(placeholder => (
          <button
            key={placeholder.userId}
            {...optionProps(value === placeholder.userId)}
            onClick={() => pick(placeholder)}
            data-testid={`claim-option-${placeholder.userId}`}
          >
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {placeholder.name}
            </span>
          </button>
        ))}
        <button
          {...optionProps(value === null)}
          onClick={() => pick(null)}
          data-testid="claim-option-none"
        >
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {t('placeholderMembers.claimNone')}
          </span>
        </button>
      </div>

      <ClaimConfirmDialog
        open={asking !== null}
        name={asking?.name ?? ''}
        groupName={groupName}
        onConfirm={() => {
          if (asking) onChange(asking.userId);
          setAsking(null);
        }}
        onCancel={() => setAsking(null)}
      />
    </div>
  );
}
